window.ReigningChampTools = window.ReigningChampTools || (() => {
  /**
   * Returns a debounced version of callback: calls are delayed by wait ms,
   * and any call made before that timer fires resets it (only the last call
   * in a burst actually runs callback).
   *
   * @param {(...args: any[]) => void} callback - Function to debounce. Must
   *   be a function.
   * @param {number} wait - Milliseconds to wait after the last call before
   *   running callback.
   * @returns {(...args: any[]) => void} Debounced function. A no-op if
   *   callback isn't a function.
   */
  const debounce = (callback = false, wait = 300) => {
    if (typeof callback !== "function") {
      console.warn("[ ReigningChampTools.debounce() ] :: callback must be a function, returning a no-op.");
      return () => {};
    }

    let timeout;

    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => callback(...args), wait);
    };
  };

  /**
   * Returns a throttled version of callback: at most one call runs per
   * delay ms, calls made within that window are dropped.
   *
   * @param {(...args: any[]) => void} callback - Function to throttle. Must
   *   be a function.
   * @param {number} delay - Minimum milliseconds between calls.
   * @returns {(...args: any[]) => void} Throttled function. A no-op if
   *   callback isn't a function.
   */
  const throttle = (callback = false, delay = 300) => {
    if (typeof callback !== "function") {
      console.warn("[ ReigningChampTools.throttle() ] :: callback must be a function, returning a no-op.");
      return () => {};
    }

    let lastCall = 0;

    return (...args) => {
      const now = new Date().getTime();

      if (now - lastCall < delay) {
        return;
      }

      lastCall = now;
      return callback(...args);
    };
  };

  /**
   * Polls a condition on an interval until it's true, then runs a callback.
   * Gives up and warns after maxRetries, instead of polling forever, so a
   * third-party SDK/app that never loads (uninstalled, blocked, offline)
   * can't leave an interval running indefinitely.
   *
   * @param {() => boolean} condition - Checked on each tick; starts the
   *   callback once it returns true. Must be a function.
   * @param {() => void} callback - Runs once when the condition is met.
   *   Must be a function.
   * @param {number} maxRetries - Number of ticks to attempt before giving up.
   * @param {number} interval - Milliseconds between ticks.
   */
  const waitForExternalApp = (condition = false, callback = false, maxRetries = 50, interval = 100) => {
    if (typeof condition !== "function" || typeof callback !== "function") {
      console.warn("[ ReigningChampTools.waitForExternalApp() ] :: condition and callback must both be functions, aborting.");
      return;
    }

    let attempts = 0;

    const poll = setInterval(() => {
      attempts++;

      if (condition()) {
        clearInterval(poll);
        callback();
        return;
      }

      if (attempts >= maxRetries) {
        clearInterval(poll);
        console.warn("[ ReigningChampTools.waitForExternalApp() ] :: condition not met in time, giving up.");
      }
    }, interval);
  };

  return { debounce, throttle, waitForExternalApp };
})();
